import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Layout from "../components/Layout";
import {
  FaHome,
  FaGraduationCap,
  FaUsers,
  FaHeart,
  FaShieldAlt,
  FaAward,
  FaStar,
  FaSchool,
  FaBook,
  FaMicroscope,
  FaFutbol,
  FaMusic,
  FaLaptop,
  FaQuoteLeft,
  FaCheckCircle,
} from "react-icons/fa";
import BreadCrumbImage from "../assets/images/breadcrumb.png";

const About = () => {
  const featureCards = [
    {
      icon: FaGraduationCap,
      title: "Academic Excellence",
      description: "Comprehensive CBSE curriculum from Pre to Class 10",
      color: "bg-gradient-to-br from-blue-50 to-blue-100",
      borderColor: "border-blue-200",
      iconColor: "text-blue-600",
      points: ["CBSE Curriculum", "Smart Classes", "Regular Tests", "Result Analysis"],
    },
    {
      icon: FaHeart,
      title: "Holistic Development",
      description: "Balanced focus on academics and personality",
      color: "bg-gradient-to-br from-emerald-50 to-emerald-100",
      borderColor: "border-emerald-200",
      iconColor: "text-emerald-600",
      points: ["Sports Activities", "Art & Music", "Life Skills", "Yoga Classes"],
    },
    {
      icon: FaLaptop,
      title: "Digital Learning",
      description: "Technology integrated smart education",
      color: "bg-gradient-to-br from-purple-50 to-purple-100",
      borderColor: "border-purple-200",
      iconColor: "text-purple-600",
      points: ["Computer Lab", "Smart Boards", "Digital Content", "Project Work"],
    },
    {
      icon: FaShieldAlt,
      title: "Safe Environment",
      description: "Secure and caring campus atmosphere",
      color: "bg-gradient-to-br from-amber-50 to-amber-100",
      borderColor: "border-amber-200",
      iconColor: "text-amber-600",
      points: ["CCTV Surveillance", "Medical Room", "Clean Campus", "Trained Staff"],
    },
  ];

  const stats = [
    { 
      value: "Pre-10", 
      label: "Complete Education", 
      icon: FaGraduationCap,
      iconColor: "text-blue-600",
      bg: "bg-blue-50",
      hoverColor: "hover:bg-blue-100"
    },
    { 
      value: "CBSE", 
      label: "Board Affiliation", 
      icon: FaAward,
      iconColor: "text-emerald-600",
      bg: "bg-emerald-50",
      hoverColor: "hover:bg-emerald-100"
    },
    { 
      value: "25+", 
      label: "Expert Faculty", 
      icon: FaUsers,
      iconColor: "text-purple-600",
      bg: "bg-purple-50",
      hoverColor: "hover:bg-purple-100"
    },
    { 
      value: "500+", 
      label: "Happy Students", 
      icon: FaHeart,
      iconColor: "text-amber-600",
      bg: "bg-amber-50",
      hoverColor: "hover:bg-amber-100"
    },
  ];

  const facilities = [
    { icon: FaBook, title: "Library", desc: "Books & Resources", color: "text-blue-600" },
    { icon: FaMicroscope, title: "Science Lab", desc: "Practical Learning", color: "text-emerald-600" },
    { icon: FaFutbol, title: "Sports", desc: "Games & Fitness", color: "text-red-600" },
    { icon: FaMusic, title: "Music Room", desc: "Arts Education", color: "text-purple-600" },
    { icon: FaLaptop, title: "Computer Lab", desc: "Digital Skills", color: "text-indigo-600" },
    { icon: FaSchool, title: "Activity Room", desc: "Creative Space", color: "text-amber-600" },
  ];

  return (
    <Layout>
      <Helmet>
        <title>About Us | School</title>
        <meta
          name="description"
          content="Quality CBSE education from Pre to Class 10. Focus on academic excellence and holistic development."
        />
      </Helmet>

      {/* Breadcrumb */}
      <div className="relative bg-gray-900">
        <div className="h-48 md:h-56">
          <img
            src={BreadCrumbImage}
            alt="Breadcrumb"
            className="object-cover w-full h-full opacity-60"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="flex items-center mb-3 text-sm text-white">
            <Link to="/" className="flex items-center transition-colors hover:text-gray-200">
              <FaHome className="w-4 h-4 mr-2" />
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="font-semibold">About Us</span>
          </div>
          <h1 className="text-3xl font-bold text-white md:text-4xl">
            About Us
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="container px-4 py-8 mx-auto md:py-12">
        
        {/* Introduction */}
        <div className="max-w-3xl mx-auto mb-8 text-center md:mb-12">
          <h2 className="mb-3 text-2xl font-bold text-gray-900 md:text-3xl">
            Welcome to Our School
          </h2>
          <p className="text-gray-700 md:text-lg">
            We provide quality CBSE education from Pre-Nursery to Class 10, focusing on 
            academic excellence, discipline, and all-round development. Our dedicated 
            faculty and modern facilities create an ideal learning environment.
          </p>
        </div>

        {/* Stats with enhanced hover */}
        <div className="grid grid-cols-2 gap-3 mb-8 md:grid-cols-4 md:gap-4 md:mb-12">
          {stats.map((stat, i) => (
            <div
              key={i}
              className={`p-4 text-center rounded-lg border border-gray-200 ${stat.bg} transition-all duration-300 hover:shadow-lg hover:scale-[1.03] ${stat.hoverColor} relative overflow-hidden group`}
            >
              {/* Subtle hover background effect */}
              <div className="absolute inset-0 transition-transform duration-700 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:translate-x-full" />
              
              <div className="relative">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${stat.bg} mb-3 group-hover:scale-110 transition-transform duration-300`}>
                  <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
                </div>
                <div className="text-xl font-bold text-gray-900 md:text-2xl">{stat.value}</div>
                <div className="text-sm text-gray-600 md:text-base">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Feature Cards with beautiful hover effects */}
        <div className="mb-8 md:mb-12">
          <h3 className="mb-6 text-xl font-bold text-center text-gray-900 md:text-2xl">
            Our Key Features
          </h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {featureCards.map((card, i) => (
              <div
                key={i}
                className={`relative overflow-hidden p-5 rounded-lg border ${card.borderColor} ${card.color} transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group`}
              >
                {/* Enhanced shine effect */}
                <div className="absolute inset-0 transition-transform duration-1000 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:translate-x-full" />
                
                <div className="relative">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`p-2 rounded-md bg-white ${card.iconColor} group-hover:scale-110 transition-transform duration-300`}>
                      <card.icon className="w-5 h-5 md:w-6 md:h-6" />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-gray-900 md:text-lg">{card.title}</h4>
                      <p className="mt-1 text-sm text-gray-700">{card.description}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {card.points.map((point, idx) => (
                      <div key={idx} className="flex items-center gap-2 transition-transform duration-200 group/item hover:translate-x-1">
                        <FaCheckCircle className={`w-3 h-3 ${card.iconColor}`} />
                        <span className="text-sm text-gray-700">{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Facilities & Information */}
        <div className="grid gap-6 mb-8 md:grid-cols-2 md:mb-12">
          {/* Facilities */}
          <div className="p-5 transition-shadow duration-300 bg-white border border-gray-200 rounded-lg hover:shadow-md">
            <div className="flex items-center gap-2 mb-4">
              <FaSchool className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold text-gray-900">Our Facilities</h3>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {facilities.map((facility, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 p-3 transition-colors duration-300 rounded-lg bg-gray-50 hover:bg-blue-50 hover:shadow-sm group"
                >
                  <div className={`p-2 rounded-md bg-white ${facility.color} group-hover:scale-110 transition-transform duration-200`}>
                    <facility.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{facility.title}</div>
                    <div className="text-xs text-gray-600">{facility.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mission & Vision */}
          <div className="space-y-4">
            <div className="p-5 transition-shadow duration-300 border border-blue-100 rounded-lg bg-blue-50 hover:shadow-sm group">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <h3 className="font-bold text-gray-900">Our Mission</h3>
              </div>
              <p className="text-sm text-gray-700">
                To provide quality education that develops academic skills, moral values, 
                and social responsibility in every student.
              </p>
            </div>
            
            <div className="p-5 transition-shadow duration-300 border rounded-lg bg-emerald-50 border-emerald-100 hover:shadow-sm group">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                <h3 className="font-bold text-gray-900">Our Vision</h3>
              </div>
              <p className="text-sm text-gray-700">
                To create a learning environment that fosters excellence, creativity, 
                and prepares students for future challenges.
              </p>
            </div>

            <div className="p-5 transition-shadow duration-300 border border-purple-100 rounded-lg bg-purple-50 hover:shadow-sm group">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <h3 className="font-bold text-gray-900">Educational Approach</h3>
              </div>
              <p className="text-sm text-gray-700">
                Child-centered learning with focus on conceptual understanding, 
                practical knowledge, and application-based education.
              </p>
            </div>
          </div>
        </div>

        {/* School Information */}
        <div className="p-5 mb-8 transition-shadow duration-300 border border-gray-200 rounded-lg bg-gray-50 md:p-6 hover:shadow-md">
          <h3 className="mb-4 text-lg font-bold text-center text-gray-900">About Our School</h3>
          <div className="grid gap-4 text-sm text-gray-700 md:grid-cols-2">
            <div>
              <p className="mb-3">
                <span className="font-semibold">Classes:</span> Pre-Nursery to Class 10
              </p>
              <p className="mb-3">
                <span className="font-semibold">Curriculum:</span> CBSE (Central Board of Secondary Education)
              </p>
              <p>
                <span className="font-semibold">Focus Areas:</span> Academics, Sports, Arts, Discipline
              </p>
            </div>
            <div>
              <p className="mb-3">
                <span className="font-semibold">Teaching Method:</span> Interactive & Activity-based
              </p>
              <p className="mb-3">
                <span className="font-semibold">Assessment:</span> Regular Tests & Progress Reports
              </p>
              <p>
                <span className="font-semibold">Environment:</span> Safe, Clean & Conducive to Learning
              </p>
            </div>
          </div>
        </div>

        {/* Quote Section */}
        <div className="p-6 transition-shadow duration-300 border border-blue-100 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-full">
              <FaQuoteLeft className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Our Philosophy</h3>
          </div>
          
          <p className="mb-2 text-sm italic text-gray-800 md:text-base">
            "Education is the foundation upon which we build our future. We aim to provide 
            quality education that shapes character and builds confident individuals."
          </p>
          <div className="text-sm font-medium text-blue-600">
            — School Values
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Committed to providing quality education and nurturing young minds since establishment.
          </p>
        </div>

      </div>
    </Layout>
  );
};

export default About;